'use client';

import { Fragment, memo, useMemo, useRef, useState } from 'react';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import type { FaqCategory } from '@/app/types';
import { cn } from '@/app/utils';
import FaqContent from './FAQContent';

interface DesktopProps {
  categories: FaqCategory[];
}

export const Desktop = memo(({ categories }: DesktopProps) => {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]?.slug || '');
  const faqContentRef = useRef<HTMLDivElement>(null);

  const height = useMemo(() => {
    return categories.length * (68 + 12) - 12;
  }, [categories]);

  const handleCategoryClick = (slug: string) => {
    setSelectedCategory(slug);

    faqContentRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const selectedCategoryData = categories.find((cat) => cat.slug === selectedCategory);

  return (
    <div className="mb-[50px] inline-flex w-[1320px] items-start justify-start gap-6 md:hidden">
      {/* Category List */}
      <div className="inline-flex w-[480px] flex-col items-start justify-start gap-3">
        {categories.map((category) => (
          <div
            key={category.slug}
            onClick={() => handleCategoryClick(category.slug)}
            className={`flex cursor-pointer flex-col items-start justify-start gap-3 self-stretch rounded-2xl bg-white px-6 py-5 transition-all ${
              selectedCategory === category.slug ? 'bg-opacity-20' : 'bg-opacity-10 hover:bg-opacity-20'
            }`}
          >
            <div className="inline-flex items-center justify-between self-stretch">
              <div className="flex w-[385.50px] items-center justify-start gap-2">
                <div className="justify-start font-semibold text-white text-xl leading-7">{category.category}</div>
              </div>
              <BaseLineRight className="h-6 w-6 text-white" />
            </div>
          </div>
        ))}
      </div>

      {/* FAQ Content */}
      <div className={cn('inline-flex flex-1 flex-col items-start justify-start gap-2')}>
        <div
          className={cn(
            'relative flex flex-col items-end justify-between gap-4 self-stretch rounded-2xl bg-white bg-opacity-20 py-8 pr-10 pl-6',
            'overflow-y-auto',
          )}
          style={{ height }}
          ref={faqContentRef}
        >
          <div className="mb-[142px] grid grid-cols-1 gap-10">
            {selectedCategoryData?.faqs?.map((faq, index) => (
              <FaqContent key={`${selectedCategoryData.slug}-faq-${index}`} faq={faq} />
            ))}
          </div>
          <div className="justify-end text-right font-semibold text-5xl text-white leading-[48px] opacity-5">
            {selectedCategoryData?.category.split(' ').map((words) => (
              <Fragment key={words}>
                <br />
                <span>{words}</span>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
